import {
  Box,
  Button,
  HeadingText,
  ImageMessage,
  StackLayout,
  TextAction,
  useMediaQuery,
  queryHelpers,
} from "@leagueplatform/genesis-core";
import type { GDSStyleObject } from "@leagueplatform/genesis-core";
import { useIntl } from "@leagueplatform/locales";
import { useForm, FormProvider } from "@leagueplatform/web-common";
import accountIcon from "assets/account.svg";
import { EXTERNAL_LINKS } from "common/links";
import { RHFCheckbox } from "components/form-elements/rhf-checkbox.component";
import { useAcceptUserTerms } from "hooks/mutations/use-accept-user-terms";
import { useUpdateCommunicationPreferences } from "hooks/mutations/use-update-communication-preferences.hook";
import { useState } from "react";

const inlineLinkStyle: GDSStyleObject = {
  typography: "$bodyOne",
  display: "inline",
  textDecoration: "underline",
};

type ConsentFormValues = {
  privacyAgreement: boolean;
  emailOptIn: boolean;
  paperReportsOptIn: boolean;
};

type ConsentStepProps = { onContinue: () => void; onError?: () => void };

export const ConsentStep = ({ onContinue, onError }: ConsentStepProps) => {
  const { formatMessage } = useIntl();
  const formMethods = useForm<ConsentFormValues>({
    defaultValues: {
      privacyAgreement: false,
      emailOptIn: false,
      paperReportsOptIn: false,
    },
  });

  const isMobile = useMediaQuery(queryHelpers.down("tablet"));

  const acceptTermsMutation = useAcceptUserTerms();
  const { updateCommunicationPreferences } =
    useUpdateCommunicationPreferences();

  const [isLoading, setLoading] = useState(false);
  const handleSubmit = async () => {
    setLoading(true);
    try {
      await acceptTermsMutation("1.0"); // TODO get actual version
      const { emailOptIn, paperReportsOptIn } = formMethods.getValues();
      await updateCommunicationPreferences({
        emailNotification: emailOptIn,
        paperReports: paperReportsOptIn,
      });
      onContinue();
    } catch (error) {
      if (onError) onError();
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormProvider formMethods={formMethods} onSubmit={handleSubmit}>
      <StackLayout>
        <ImageMessage
          image={accountIcon}
          css={{ padding: isMobile ? "$oneAndHalf" : "$two" }}
        >
          <HeadingText
            level="2"
            size="xl"
            css={{ textAlign: "center", paddingBlockEnd: "$two" }}
          >
            {formatMessage({ id: "STR_ACCOUNT_SETUP_COMPLETE_YOUR_ACCOUNT" })}
          </HeadingText>
          <StackLayout
            spacing="$oneAndHalf"
            css={{ width: "100%", textAlign: "left" }}
          >
            <RHFCheckbox
              id="privacyAgreement"
              required
              requiredMessage={formatMessage({
                id: "STR_ACCOUNT_SETUP_PRIVACY_REQUIRED",
              })}
              label={formatMessage(
                { id: "STR_ACCOUNT_SETUP_PRIVACY_AGREEMENT" },
                {
                  privacyNoticesLink: (
                    <TextAction
                      target="_blank"
                      rel="noopener noreferrer"
                      as="a"
                      href={EXTERNAL_LINKS.PRIVACY}
                      css={inlineLinkStyle}
                    >
                      {formatMessage({ id: "STR_PRIVACY_POLICY" })}
                    </TextAction>
                  ),
                  termsAndConditionsLink: (
                    <TextAction
                      target="_blank"
                      rel="noopener noreferrer"
                      as="a"
                      href={EXTERNAL_LINKS.TERMS}
                      css={inlineLinkStyle}
                    >
                      {formatMessage({ id: "STR_TERMS_OF_SERVICE" })}
                    </TextAction>
                  ),
                }
              )}
            />
            <RHFCheckbox
              id="emailOptIn"
              label={formatMessage({ id: "STR_ACCOUNT_SETUP_EMAIL_OPT_IN" })}
            />
            <RHFCheckbox
              id="paperReportsOptIn"
              label={formatMessage({
                id: "STR_ACCOUNT_SETUP_PAPER_REPORTS_OPT_IN",
              })}
            />
          </StackLayout>
        </ImageMessage>
        <StackLayout
          horizontalAlignment="end"
          orientation="horizontal"
          css={{
            borderTop: "1px solid $onSurfaceBorderSubdued",
            paddingBlock: "$one",
            paddingInline: isMobile ? "$oneAndHalf" : "$two",
            width: "100%",
          }}
        >
          {/* Button content loses alignment if a fixed width is set directly on Button. */}
          <Box css={{ minWidth: isMobile ? "100%" : "220px" }}>
            <Button type="submit" width="fillContainer" loading={isLoading}>
              {formatMessage({ id: "STR_CONTINUE" })}
            </Button>
          </Box>
        </StackLayout>
      </StackLayout>
    </FormProvider>
  );
};
